// Анимация ожидания: паровая труба обхватывает барабан, пока тот крутится один.
//
// Рамка падает на третий барабан, когда два скаттера уже легли на первый и второй,
// см. GDD, раздел 3.5. Барабан под ней крутится дольше остальных, вентиль проворачивается,
// и из коленьев трубы бьёт пар. Барабан встал — рамка уходит.
//
// Правило и показ лежат в одном модуле: правило говорит, на каком барабане ждать, а показ
// ставит на него рамку. Оба знают об ожидании и больше ни о чём.
//
// Проём рамки нарисован ровно под окно барабана, поэтому рамка встаёт на колонку без подгонки,
// см. ANTICIPATION_OPENING. Своих координат она не считает: середину колонки ей называет поле.

import { Container } from 'pixi.js';

import type { GameConfig, GameSymbol, SymbolId } from '../api/gameConfig';
import type { Field, FieldRow, SpinResult } from '../api/spin';
import { ANTICIPATION_OPENING, ANTICIPATION_SKELETON, REELS_GRID } from '../assets/catalog';
import type { AssetManager, Spine } from '../assets/manager';
import type { SoundVoice } from '../sound/engine';
import { sfx } from '../sound/sound';

import { columnX } from './reels';
import { animationSeconds } from './scene';

/**
 * За сколько уходит шипение, когда рамку снимает скип.
 *
 * Рамка при этом пропадает разом — игрок торопит показ. Звук так пропасть не может:
 * оборванный на полуслове, он слышен щелчком, см. ADR-50.
 */
const HUSH_SECONDS: number = 0.15;

/**
 * Сколько барабан ожидания крутится сверх своей очереди.
 *
 * За это время рамка успевает упасть и пару раз выпустить пар: падение идёт 1.1 секунды,
 * а круг пара — меньше половины. Дольше держать нечего, игрок уже прочитал два скаттера.
 */
export const HOLD_SECONDS: number = 2;

/**
 * Что делает рамка.
 *
 * hidden — рамки на поле нет.
 * shown — рамка упала на барабан и выпускает пар.
 * out — рамка уходит с барабана.
 */
type FramePhase = 'hidden' | 'shown' | 'out';

/**
 * Барабан, на котором игра ждёт скаттер.
 *
 * Ждёт она тот барабан, до которого скаттеры дошли по очереди от левого края и на котором
 * последнего для бонуса не хватает ровно одного, см. GDD, разделы 3.5 и 3.6. На поле 3x3
 * с тремя скаттерами это третий барабан, когда первый и второй показали по скаттеру.
 *
 * Барабан со сдвигом ожидания не получает: он не крутится, а съезжает на символ вниз,
 * и ждать на нём нечего, см. Nudge Respin в GDD, раздел 4.3.
 *
 * @param scatter Символ скаттера.
 * @param needed Сколько скаттеров на экране запускают бонус.
 * @returns Номер барабана слева направо. Null — ждать на этом спине нечего.
 */
export function anticipatedReel(spin: SpinResult, scatter: SymbolId, needed: number): number | null {
  const reels: number = spin.field[0]?.length ?? 0;
  const reel: number = needed - 1;

  if (reel < 1 || reel >= reels)
    return null;

  if (spin.nudged.includes(reel))
    return null;

  let seen = 0;

  for (let column = 0; column < reel; column++) {
    const found: number = scattersOn(spin.field, column, scatter);

    // Скаттеры идут по очереди от левого края: пропущенный барабан рвёт очередь, и ждать
    // на этом спине уже нечего.
    if (found === 0)
      return null;

    seen += found;
  }

  return seen === needed - 1 ? reel : null;
}

/** Сколько скаттеров лежит на одном барабане. */
function scattersOn(field: Field, column: number, scatter: SymbolId): number {
  return field.reduce((count: number, row: FieldRow) => count + (row[column] === scatter ? 1 : 0), 0);
}

/** Символ скаттера в описании игры. Null — скаттера в слоте нет. */
export function scatterSymbol(config: GameConfig): SymbolId | null {
  return config.symbols.find((symbol: GameSymbol) => symbol.role === 'scatter')?.id ?? null;
}

/**
 * Рамка ожидания на барабане.
 *
 * Слот без скаттера или без бонуса за скаттеры её не заводит: ждать в нём нечего,
 * см. createAnticipation.
 */
export class Anticipation {
  readonly view: Container = new Container();

  readonly seconds: number = HOLD_SECONDS;

  readonly #frame: Spine;
  readonly #reels: number;
  readonly #scatter: SymbolId;
  readonly #needed: number;

  #phase: FramePhase = 'hidden';
  #seconds = 0;

  /** Идущее шипение вентиля. Null — звука нет, и уводить нечего. */
  #hiss: SoundVoice | null = null;

  constructor(config: GameConfig, assets: AssetManager, scatter: SymbolId, needed: number) {
    this.#reels = config.reels.length;
    this.#scatter = scatter;
    this.#needed = needed;

    this.#frame = assets.spine(ANTICIPATION_SKELETON);
    this.#frame.visible = false;

    this.view.addChild(this.#frame);
  }

  /** Барабан, который тянет остановку на этом спине. Null — тянуть нечего. */
  reelFor(spin: SpinResult): number | null {
    return anticipatedReel(spin, this.#scatter, this.#needed);
  }

  /**
   * Роняет рамку на заданный барабан.
   *
   * Падение переходит в круг пара само: обе анимации ставятся на дорожку разом, и круг
   * повторяется, пока барабан не встанет.
   *
   * Шипение вентиля идёт вместе с рамкой и уходит вместе с ней: пар без звука нем, звук
   * за снятой рамкой беспричинен, см. ADR-49 и ADR-50.
   */
  show(reel: number): void {
    const { intro, loop } = ANTICIPATION_SKELETON.animations;

    this.#place(reel);
    this.#phase = 'shown';
    this.#seconds = 0;

    this.#frame.visible = true;
    this.#frame.state.setAnimation(0, intro, false);
    this.#frame.state.addAnimation(0, loop, true, 0);

    this.#hiss = sfx('anticipation');
  }

  /** Уводит рамку с барабана. Рамки на поле нет — уводить нечего. */
  hide(): void {
    if (this.#phase !== 'shown')
      return;

    this.#phase = 'out';
    this.#seconds = 0;
    this.#frame.state.setAnimation(0, ANTICIPATION_SKELETON.animations.out, false);

    // Шипение уходит вместе с рамкой, за то же время: барабан встал, и ждать больше нечего.
    this.#silence(animationSeconds(this.#frame, ANTICIPATION_SKELETON.animations.out));
  }

  /** Снимает рамку разом: игрок торопит показ, и досматривать её уход он не должен. */
  skip(): void {
    this.#phase = 'hidden';
    this.#seconds = 0;
    this.#frame.visible = false;

    // Рамку игрок оборвал сам, а звук уходит не рывком: обрыв на полуслове слышен щелчком,
    // см. ADR-50.
    this.#silence(HUSH_SECONDS);
  }

  update(seconds: number): void {
    if (this.#phase !== 'out')
      return;

    this.#seconds += seconds;

    if (this.#seconds < animationSeconds(this.#frame, ANTICIPATION_SKELETON.animations.out))
      return;

    this.skip();
  }

  /** Уводит шипение за заданное время. Звука нет — уводить нечего. */
  #silence(seconds: number): void {
    this.#hiss?.fadeOut(seconds);
    this.#hiss = null;
  }

  /**
   * Ставит рамку проёмом на окно барабана.
   *
   * Проём нарисован под окно поля, поэтому масштаб выходит единичным. Считается он всё равно:
   * сетка окон задана в каталоге и может разойтись с ассетом, а рамка мимо окна читается сразу.
   *
   * Ось Y у Spine смотрит вверх, у Pixi вниз, поэтому по вертикали смещение прибавляется.
   */
  #place(reel: number): void {
    const scale: number = REELS_GRID.windowWidth / ANTICIPATION_OPENING.width;

    const x: number = columnX(reel, this.#reels);
    const y: number = REELS_GRID.main.centerY;

    this.#frame.scale.set(scale);
    this.#frame.position.set(x - ANTICIPATION_OPENING.x * scale, y + ANTICIPATION_OPENING.y * scale);
  }
}

/**
 * Заводит рамку ожидания по описанию игры.
 *
 * @returns Null, если ждать в слоте нечего: скаттера нет или бонус за скаттеры не описан.
 */
export function createAnticipation(config: GameConfig, assets: AssetManager): Anticipation | null {
  const scatter: SymbolId | null = scatterSymbol(config);
  const needed: number | undefined = config.redHotSpin?.scatters;

  if (scatter === null || needed === undefined)
    return null;

  return new Anticipation(config, assets, scatter, needed);
}
